import Header from "./components/header";
import "./App.css";
import Blog from "./components/blog";
import BlogPostPage from "./components/BlogPostPage";
import BlogPostEntry from "./components/blog-input";
import ContactForm from "./components/ContactForm";
import { useEffect, useState } from "react";
import LeftMenu, { ViewKey } from "./components/LeftMenu";
import MusicBoombox from "./components/MusicBoombox";
import WebsitesList from "./components/WebsitesList";
import { useAuth } from "./auth";
import UserAccountDisplay from "./components/UserAccountDisplay";

function App() {
  const { isLoggedIn } = useAuth();
  const [searchText, setSearchText] = useState("");
  // Secret blog entry modal state
  const [entryModalOpen, setEntryModalOpen] = useState(false);
  const [activeView, setActiveView] = useState<ViewKey>("blog");
  const [singlePostId, setSinglePostId] = useState<string | null>(null);
  const [wDown, setWDown] = useState(false);
  const [eDown, setEDown] = useState(false);
  // Update this list to add more rotating logo images
  const logoImages = ["seeward.jpg"];
  const [logoIndex, setLogoIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setLogoIndex((i) => (i + 1) % logoImages.length);
    }, 30000); // swap every 10 seconds
    return () => clearInterval(id);
  }, [logoImages.length]);

  // Track pressed keys globally to detect W+E chord
  useEffect(() => {
    // Lightweight hash router: #/blog -> list, #/blog/:id -> single post
    const parseHash = () => {
      try {
        const h = window.location.hash || "";
        const m = h.match(/^#\/blog\/(.+)$/);
        if (m && m[1]) {
          return decodeURIComponent(m[1]);
        }
        return null;
      } catch {
        return null;
      }
    };
    const apply = () => {
      const id = parseHash();
      setSinglePostId(id);
      if (id) setActiveView("blog");
    };
    apply();
    const onHash = () => apply();
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    // If user navigates to other sections, ensure single-post is cleared
    if (activeView !== "blog") {
      setSinglePostId(null);
    }
  }, [activeView]);

  const backToBlogList = () => {
    if (typeof window !== "undefined") {
      window.location.hash = "#/blog";
    }
  };

  useEffect(() => {
    const onKeyDown = (ev: KeyboardEvent) => {
      const k = ev.key.toLowerCase();
      if (k === "w") setWDown(true);
      if (k === "e") setEDown(true);
    };
    const onKeyUp = (ev: KeyboardEvent) => {
      const k = ev.key.toLowerCase();
      if (k === "w") setWDown(false);
      if (k === "e") setEDown(false);
    };
    const onBlur = () => {
      setWDown(false);
      setEDown(false);
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
    };
  }, []);

  const handleContactClick = (e: React.MouseEvent) => {
    // Secret chord: open blog entry modal instead
    if (wDown && eDown) {
      e.preventDefault();
      if (!isLoggedIn) {
        alert("Please sign in with Google to use admin features.");
        return;
      }
      setEntryModalOpen(true);
      return;
    }
    // Default: show the contact form and hide the blog column
    e.preventDefault();
    setActiveView("contact");
    // Scroll the panel to top for good measure
    const panel = document.querySelector(".main-panel") as HTMLElement | null;
    if (panel) panel.scrollTop = 0;
  };

  // Lock underlying scroll when modal is open
  useEffect(() => {
    const panel = document.querySelector(".main-panel") as HTMLElement | null;
    if (!panel) return;
    const prevOverflow = panel.style.overflow;
    const prevOverflowY = panel.style.overflowY;
    const prevOverscroll = (panel.style as any).overscrollBehavior;
    if (entryModalOpen) {
      panel.style.overflow = "hidden";
      (panel.style as any).overscrollBehavior = "contain";
    } else {
      panel.style.overflowY = "auto";
      panel.style.overflow = "";
      (panel.style as any).overscrollBehavior = "";
    }
    return () => {
      // restore previous styles
      panel.style.overflow = prevOverflow;
      panel.style.overflowY = prevOverflowY;
      (panel.style as any).overscrollBehavior = prevOverscroll;
    };
  }, [entryModalOpen]);

  return (
    <div className="App">
      <Header searchValue={searchText} onSearchChange={setSearchText} onContactClick={handleContactClick} />
      <div className="main-panel">
        <div className={`layout-grid`}>
          <div>
            {isLoggedIn && <UserAccountDisplay />}
            <LeftMenu
              active={activeView}
              onSelect={(v) => {
                if (v === "contact") {
                  // mirror header behavior (including secret chord via header handler)
                  setActiveView("contact");
                } else {
                  setActiveView(v);
                }
                const panel = document.querySelector(".main-panel") as HTMLElement | null;
                if (panel) panel.scrollTop = 0;
              }}
            />
            {/* Boombox now lives inside the Music section */}
            <div className="corner-logo-wrap">
              <img src={logoImages[logoIndex]} alt="Seeward logo" className="corner-logo" />
            </div>
            {/* Bottom-right sticky arm tattoo */}
            <div className="corner-tattoo-wrap" aria-hidden>
              <img src="/arm-tattoo.png" alt="" className="corner-tattoo" />
            </div>
          </div>
          <div className="blog-col">
            {activeView === "contact" && <ContactForm onClose={() => setActiveView("blog")} />}
            {activeView === "music" && <MusicBoombox onBack={() => setActiveView("blog")} />}
            {activeView === "websites" && <WebsitesList onBack={() => setActiveView("blog")} />}
            {activeView === "blog" && (singlePostId ? <BlogPostPage postId={singlePostId} onBack={backToBlogList} /> : <Blog filterText={searchText} />)}
          </div>
        </div>
      </div>
      {entryModalOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Blog entry">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">New Blog Entry</h2>
              <button className="modal-close" aria-label="Close" onClick={() => setEntryModalOpen(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <BlogPostEntry />
            </div>
          </div>
        </div>
      )}
      {/* Mobile scroll-to-top button */}
      <button
        className="scroll-top-btn"
        aria-label="Scroll to top"
        onClick={() => {
          if (typeof window !== "undefined") {
            const panel = document.querySelector(".main-panel") as HTMLElement | null;
            if (panel && typeof (panel as any).scrollTo === "function") {
              (panel as any).scrollTo({ top: 0, behavior: "smooth" });
            } else if (panel) {
              panel.scrollTop = 0;
            }
          }
        }}
      >
        <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
          <path d="M12 5l-7 7h4v7h6v-7h4z" fill="currentColor" />
        </svg>
      </button>
      <style>
        {`
          /* brutalist layout + corner logo placement */
          .layout-grid {
            width: min(1800px, 100%);
            margin: 0 auto;
            display: grid;
            grid-template-columns: 26rem 1fr; /* even wider left column for boombox */
            gap: 24px;
            align-items: stretch; /* ensure columns share full row height */
            padding: 24px 16px 48px;
            box-sizing: border-box;
          }

          /* On large screens, position the grid to bring menu closer to left edge */
          @media (min-width: 1400px) {
            .layout-grid {
              margin-left: max(32px, calc((100vw - 1400px) / 6));
              margin-right: auto;
              /* This progressively moves the grid leftward as screen gets larger */
            }
          }

          @media (min-width: 1800px) {
            .layout-grid {
              margin-left: max(48px, calc((100vw - 1800px) / 4));
              /* Even more leftward positioning on very large screens */
            }
          }

          /* Stretch only the left column in case align-items changes later */
          .layout-grid > div:first-child { align-self: stretch; }

          /* removed admin-open third column */

          .corner-logo-wrap {
            position: relative;
            min-height: 60vh;
          }

          .corner-logo {
            position: fixed;
            left: calc(clamp(18px, 2vw, 24px) + 20px);
            bottom: clamp(8px, 2vw, 24px);
            width: 220px;
            /* Keep the logo a consistent square regardless of image size */
            aspect-ratio: 1 / 1;
            height: auto; /* computed from aspect-ratio */
            object-fit: contain; /* show full image, no cropping */
            background: #fff;
            z-index: -1; /* ensure always beneath overlapping elements */
            pointer-events: none; /* never block interactions */
          }

          /* Adjust logo position on large screens to align with shifted layout */
          @media (min-width: 1400px) {
            .corner-logo {
              left: calc(max(32px, calc((100vw - 1400px) / 6)) + 20px);
            }
          }

          @media (min-width: 1800px) {
            .corner-logo {
              left: calc(max(48px, calc((100vw - 1800px) / 4)) + 20px);
            }
          }

          .blog-col { 
            min-width: 0; 
            margin-left: 50px;
          }
          .entry-col { min-width: 280px; }

          /* bottom-right tattoo fixed to viewport (page-wide) */
          .corner-tattoo-wrap {
            position: fixed;
            right: 0px; /* ~20px from right edge */
            bottom: 30px;   /* sit on the bottom edge */
            pointer-events: none; /* decorative only; don't block clicks */
            z-index: -2; /* behind all page content (and logo at -1) */
          }
          .corner-tattoo {
            display: block;
            width: 154px; /* 30% smaller than 220px */
            height: auto;
            object-fit: contain;
          }

          /* responsive behavior */
          @media (max-width: 1100px) {
            .layout-grid { grid-template-columns: 30rem 1fr; }
            .entry-col { grid-column: 1 / -1; }
          }

          @media (max-width: 760px) {
            .layout-grid { grid-template-columns: 1fr; }
            /* hide corner logo entirely on small screens */
            .corner-logo-wrap, .corner-logo { display: none !important; }
            /* hide tattoo on very small screens to avoid overlap */
            .corner-tattoo-wrap, .corner-tattoo { display: none !important; }
            /* ensure admin column can't force overflow */
            .entry-col { min-width: 0; }
          }

          /* Scroll-to-top floating button */
          .scroll-top-btn {
            position: fixed;
            right: clamp(12px, 3vw, 18px);
            bottom: clamp(12px, 3vw, 18px);
            display: none; /* hidden by default; shown on mobile */
            align-items: center;
            justify-content: center;
            width: 52px;
            height: 52px;
            border-radius: 50%;
            background: #fff;
            color: #000;
            border: 3px solid #000;
            box-shadow: 5px 5px 0 #000;
            font-weight: 900;
            cursor: pointer;
            z-index: 2000;
          }
          .scroll-top-btn:active {
            transform: translate(-2px, -2px);
            box-shadow: 7px 7px 0 #000;
          }
          @media (max-width: 760px) {
            .scroll-top-btn { display: inline-flex; }
          }
        `}
      </style>
      <style>
        {`
          .modal-overlay {
            position: fixed;
            inset: 0;
            background: rgba(0,0,0,0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 3000;
            padding: 24px;
            /* prevent scroll chaining to page on iOS/Android */
            overscroll-behavior: contain;
          }
          .modal-content {
            width: min(1000px, 100%);
            background: #fff;
            border: 6px solid #000;
            box-shadow: 10px 10px 0 #000;
            padding: 16px;
            /* Constrain height and make body scroll within */
            display: flex;
            flex-direction: column;
            max-height: calc(100vh - 48px);
            overflow: hidden;
          }
          .modal-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 8px;
          }
          .modal-title {
            margin: 0;
          }
          .modal-close {
            border: 3px solid #000;
            background: #fff;
            font-weight: 900;
            width: 36px;
            height: 36px;
            cursor: pointer;
            box-shadow: 4px 4px 0 #000;
          }
          .modal-close:hover {
            transform: translate(-2px, -2px);
            box-shadow: 6px 6px 0 #000;
          }
          .modal-body {
            /* Scroll inside the modal, not the page */
            flex: 1 1 auto;
            min-height: 0; /* allows flex child to shrink for scrolling */
            overflow: auto;
            -webkit-overflow-scrolling: touch;
            overscroll-behavior: contain;
          }
          /* Ensure PrimeReact overlays render above the modal overlay */
          .p-dropdown-panel,
          .p-datepicker {
            z-index: 4000 !important;
          }
          /* Make overlay panels readable with solid backgrounds */
          .p-dropdown-panel,
          .p-dropdown-items,
          .p-datepicker,
          .p-datepicker .p-datepicker-group,
          .p-datepicker-header,
          .p-datepicker-calendar {
            background: #fff !important;
          }
          /* Strong borders + shadow to match brutalist style */
          .p-dropdown-panel,
          .p-datepicker {
            border: 3px solid #000 !important;
            box-shadow: 8px 8px 0 #000 !important;
          }
          /* Dropdown items hover/active styles */
          .p-dropdown-items .p-dropdown-item { background: #fff; }
          .p-dropdown-items .p-dropdown-item.p-highlight,
          .p-dropdown-items .p-dropdown-item:hover { background: #f3f3f3; }
        `}
      </style>
    </div>
  );
}

export default App;
